import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  alpha,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "components/iconify";
import MenuPopover from "components/menu-popover";
import { useSnackbar } from "components/snackbar";
import QuantityIncreaseDialog from "pages/store/orders/component/QuantityIncreaseDialog";
import {
  getStoreNotificationUnreadCountAsync,
  getStoreNotificationsAsync,
  viewAllStoreNotificationsAsync,
  viewStoreNotificationAsync,
} from "redux/notifications/notifications.async";
import { receiveStoreNotification } from "redux/notifications/notifications.slice";

const smallChipSx = {
  height: 18,
  fontSize: 9.5,
  fontWeight: 600,
  borderRadius: 0.75,
  "& .MuiChip-label": {
    px: 0.75,
  },
};

const getNotificationId = (notification) =>
  notification?.id ?? notification?._id ?? notification?.notificationId;

const getNotificationTitle = (notification) =>
  notification?.title || notification?.heading || "New notification";

const getNotificationMessage = (notification) =>
  notification?.message || notification?.body || notification?.description;

const getRealtimeNotification = (payload) =>
  payload?.notification || payload?.data || payload;

const getNotificationOrderId = (notification) =>
  notification?.order_id ||
  notification?.storeOrderId ||
  notification?.store_order_id ||
  notification?.orderId ||
  notification?.order?.id ||
  notification?.storeOrder?.id;

const getNotificationDate = (notification) => {
  const value =
    notification?.createdAt ||
    notification?.created_at ||
    notification?.updatedAt ||
    notification?.time;

  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusColor = (status = "") => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus.includes("fail")) return "error";
  if (
    normalizedStatus.includes("success") ||
    normalizedStatus.includes("paid")
  ) {
    return "success";
  }
  if (normalizedStatus.includes("pending")) return "warning";
  return "default";
};

function NotificationCard({
  notification,
  index,
  onNotificationClick,
  onOpenOrderDetails,
}) {
  const notificationId = getNotificationId(notification);
  const isUnread = !notification?.isViewed;
  const paymentStatus = notification?.paymentStatus;
  const orderStatus = notification?.orderStatus;
  const orderId = getNotificationOrderId(notification);

  return (
    <Box
      key={notificationId || notification?.createdAt || index}
      onClick={() => onNotificationClick(notification)}
      sx={{
        display: "flex",
        gap: 1,
        p: 1,
        borderRadius: 1.25,
        cursor: isUnread ? "pointer" : "default",
        border: (theme) =>
          `1px solid ${
            isUnread
              ? alpha(theme.palette.primary.main, 0.22)
              : theme.palette.divider
          }`,
        bgcolor: (theme) =>
          isUnread
            ? alpha(theme.palette.primary.main, 0.06)
            : "background.paper",
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          color: "primary.main",
        }}
      >
        <Iconify icon="eva:shopping-bag-outline" width={16} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontSize: 11.5, lineHeight: 1.25 }}
          >
            {getNotificationTitle(notification)}
          </Typography>

          <Stack direction="row" spacing={0.25} alignItems="center">
            {isUnread && (
              <Chip
                label="Recent"
                size="small"
                color="primary"
                variant="outlined"
                sx={smallChipSx}
              />
            )}

            {orderId && (
              <Tooltip title="View order details">
                <IconButton
                  size="small"
                  onClick={(event) => onOpenOrderDetails(event, notification)}
                  sx={{
                    width: 22,
                    height: 22,
                    color: "text.secondary",
                  }}
                >
                  <Iconify icon="eva:eye-outline" width={14} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {getNotificationMessage(notification) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.35, fontSize: 10.5, lineHeight: 1.35 }}
          >
            {getNotificationMessage(notification)}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 0.75, rowGap: 0.5 }}
        >
          {/* {notification?.vedaOrderId && (
            <Chip
              label={notification.vedaOrderId}
              size="small"
              variant="outlined"
            />
          )} */}

          {notification?.amount && (
            <Chip
              label={`₹${notification.amount}`}
              size="small"
              variant="outlined"
              sx={smallChipSx}
            />
          )}

          {paymentStatus && (
            <Chip
              label={paymentStatus}
              size="small"
              color={getStatusColor(paymentStatus)}
              variant="outlined"
              sx={smallChipSx}
            />
          )}

          {/* {orderStatus && (
            <Chip
              label={orderStatus}
              size="small"
              color={getStatusColor(orderStatus)}
              variant="outlined"
            />
          )} */}
        </Stack>

        {getNotificationDate(notification) && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              display: "block",
              mt: 0.75,
              fontSize: 9.5,
              lineHeight: 1.2,
            }}
          >
            {getNotificationDate(notification)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function NotificationPopover() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { userinfo } = useSelector((state) => state.userinfo);
  const {
    notifications,
    unreadCount,
    notificationLoader,
    notificationUpdateLoader,
  } = useSelector((state) => state.notifications);

  const [openPopover, setOpenPopover] = useState(null);
  const [socket, setSocket] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [allNotificationsDialog, setAllNotificationsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const isPopoverOpenRef = useRef(false);

  const adminId = userinfo?.id || userinfo?._id;
  const baseUrl =
    process.env.REACT_APP_BASE_URL || "https://api.vedaacademy.org.in";

  const displayedUnreadCount = useMemo(() => {
    if (!unreadCount) return 0;
    return unreadCount > 99 ? "99+" : unreadCount;
  }, [unreadCount]);

  const loadNotifications = () => {
    dispatch(getStoreNotificationsAsync());
    dispatch(getStoreNotificationUnreadCountAsync());
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    isPopoverOpenRef.current = Boolean(openPopover);
  }, [openPopover]);

  useEffect(() => {
    if (!adminId) {
      return undefined;
    }

    const newSocket = io(baseUrl, {
      reconnectionAttempts: 10,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 5000,
      // transports: ["polling", "websocket"],
      transports: ["polling"],
      upgrade: true,
      rejectUnauthorized: false,
      path: "/store/socket.io",
    });

    setSocket(newSocket);

    newSocket.on("connect", () => {
      newSocket.emit("join-admin-room", adminId);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [adminId, baseUrl]);

  useEffect(() => {
    if (!socket) {
      return undefined;
    }

    const handleNewOrderNotification = (payload) => {
      const notification = getRealtimeNotification(payload);

      dispatch(receiveStoreNotification(notification));
      dispatch(getStoreNotificationUnreadCountAsync());

      if (isPopoverOpenRef.current) {
        dispatch(getStoreNotificationsAsync());
      }

      enqueueSnackbar(notification?.title || "New store notification", {
        variant: "info",
      });
    };

    socket.on("new_order_notification", handleNewOrderNotification);

    return () => {
      socket.off("new_order_notification", handleNewOrderNotification);
    };
  }, [socket, dispatch, enqueueSnackbar]);

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
    loadNotifications();
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleNotificationClick = (notification) => {
    const notificationId = getNotificationId(notification);

    if (!notificationId || notification.isViewed) return;

    dispatch(viewStoreNotificationAsync({ id: notificationId }));
  };

  const handleViewAll = () => {
    dispatch(viewAllStoreNotificationsAsync());
  };

  const handleOpenAllNotifications = () => {
    setAllNotificationsDialog(true);
    handleClosePopover();
    loadNotifications();
  };

  const handleCloseAllNotifications = () => {
    setAllNotificationsDialog(false);
  };

  const handleOpenOrderDetails = (event, notification) => {
    event.stopPropagation();

    const orderId = getNotificationOrderId(notification);

    if (!orderId) return;

    setSelectedOrder({ id: orderId });
    setOrderDialog(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDialog(false);
    setSelectedOrder(null);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color={openPopover ? "primary" : "default"}
          onClick={handleOpenPopover}
          sx={{
            width: 40,
            height: 40,
            color: "text.secondary",
          }}
        >
          <Badge
            badgeContent={displayedUnreadCount}
            color="error"
            invisible={!unreadCount}
            max={99}
          >
            <Iconify icon="eva:bell-outline" width={22} />
          </Badge>
        </IconButton>
      </Tooltip>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="top-right"
        sx={{
          width: 330,
          p: 0,
          borderRadius: 1.5,
          overflow: "hidden",
        }}
      >
        <Stack spacing={1.25} sx={{ p: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
              Notifications
            </Typography>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={loadNotifications}
                  disabled={notificationLoader}
                  sx={{ width: 28, height: 28 }}
                >
                  <Iconify icon="eva:refresh-outline" width={15} />
                </IconButton>
              </Tooltip>

              <Button
                size="small"
                onClick={handleViewAll}
                disabled={!unreadCount || notificationUpdateLoader}
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  minWidth: "auto",
                  px: 0.75,
                }}
              >
                Mark all read
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 0.25 }}>
            {notificationLoader && notifications.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : notifications.length === 0 ? (
              <Stack alignItems="center" spacing={0.75} sx={{ py: 3 }}>
                <Iconify
                  icon="eva:bell-off-outline"
                  width={28}
                  sx={{ color: "text.disabled" }}
                />
                <Typography variant="caption" color="text.secondary">
                  No notifications yet
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={0.75}>
                {notifications.map((notification, index) => {
                  const notificationId = getNotificationId(notification);
                  const isUnread = !notification?.isViewed;
                  const paymentStatus = notification?.paymentStatus;
                  const orderStatus = notification?.orderStatus;
                  const orderId = getNotificationOrderId(notification);

                  return (
                    <Box
                      key={notificationId || notification?.createdAt || index}
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        display: "flex",
                        gap: 1,
                        p: 1,
                        borderRadius: 1.25,
                        cursor: isUnread ? "pointer" : "default",
                        border: (theme) =>
                          `1px solid ${
                            isUnread
                              ? alpha(theme.palette.primary.main, 0.22)
                              : theme.palette.divider
                          }`,
                        bgcolor: (theme) =>
                          isUnread
                            ? alpha(theme.palette.primary.main, 0.06)
                            : "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.12),
                          color: "primary.main",
                        }}
                      >
                        <Iconify icon="eva:shopping-bag-outline" width={16} />
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontSize: 11.5, lineHeight: 1.25 }}
                          >
                            {getNotificationTitle(notification)}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.25}
                            alignItems="center"
                          >
                            {isUnread && (
                              <Chip
                                label="Recent"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={smallChipSx}
                              />
                            )}

                            {orderId && (
                              <Tooltip title="View order details">
                                <IconButton
                                  size="small"
                                  onClick={(event) =>
                                    handleOpenOrderDetails(event, notification)
                                  }
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    color: "text.secondary",
                                  }}
                                >
                                  <Iconify icon="eva:eye-outline" width={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>

                        {getNotificationMessage(notification) && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.35, fontSize: 10.5, lineHeight: 1.35 }}
                          >
                            {getNotificationMessage(notification)}
                          </Typography>
                        )}

                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ mt: 0.75, rowGap: 0.5 }}
                        >
                          {/* {notification?.vedaOrderId && (
                            <Chip
                              label={notification.vedaOrderId}
                              size="small"
                              variant="outlined"
                            />
                          )} */}

                          {notification?.amount && (
                            <Chip
                              label={`₹${notification.amount}`}
                              size="small"
                              variant="outlined"
                              sx={smallChipSx}
                            />
                          )}

                          {paymentStatus && (
                            <Chip
                              label={paymentStatus}
                              size="small"
                              color={getStatusColor(paymentStatus)}
                              variant="outlined"
                              sx={smallChipSx}
                            />
                          )}

                          {/* {orderStatus && (
                            <Chip
                              label={orderStatus}
                              size="small"
                              color={getStatusColor(orderStatus)}
                              variant="outlined"
                            />
                          )} */}
                        </Stack>

                        {getNotificationDate(notification) && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              display: "block",
                              mt: 0.75,
                              fontSize: 9.5,
                              lineHeight: 1.2,
                            }}
                          >
                            {getNotificationDate(notification)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {notifications.length > 0 && (
            <>
              <Divider />

              <Button
                fullWidth
                size="small"
                variant="text"
                onClick={handleOpenAllNotifications}
                sx={{ fontSize: 11.5, fontWeight: 700 }}
              >
                View all
              </Button>
            </>
          )}
        </Stack>
      </MenuPopover>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={allNotificationsDialog}
        onClose={handleCloseAllNotifications}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1.5,
          }}
        >
          <Typography variant="h6">Notifications</Typography>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={loadNotifications}
                disabled={notificationLoader}
              >
                <Iconify icon="eva:refresh-outline" width={18} />
              </IconButton>
            </Tooltip>

            <IconButton size="small" onClick={handleCloseAllNotifications}>
              <Iconify icon="eva:close-outline" width={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.5 }}>
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 0.25 }}>
            {notificationLoader && notifications.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 5 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : notifications.length === 0 ? (
              <Stack alignItems="center" spacing={0.75} sx={{ py: 5 }}>
                <Iconify
                  icon="eva:bell-off-outline"
                  width={28}
                  sx={{ color: "text.disabled" }}
                />
                <Typography variant="caption" color="text.secondary">
                  No notifications yet
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={0.75}>
                {notifications.map((notification, index) => (
                  <NotificationCard
                    key={
                      getNotificationId(notification) ||
                      notification?.createdAt ||
                      index
                    }
                    notification={notification}
                    index={index}
                    onNotificationClick={handleNotificationClick}
                    onOpenOrderDetails={handleOpenOrderDetails}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <QuantityIncreaseDialog
        handleClose={handleCloseOrderDetails}
        packageInfo={selectedOrder}
        openDialog={orderDialog}
      />
    </>
  );
}
